import { useState, useEffect } from 'react';
//import { motion } from 'framer-motion';
import { getAllRestaurants, updateRestaurantStatus, getRestaurantDetails } from '../../utils/superAdminApi';
import { FaSearch, FaSync, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaEllipsisV } from 'react-icons/fa';

const statusStyles = {
  active: { icon: <FaCheckCircle className="text-green-500" />, text: 'Active', color: 'text-green-300' },
  suspended: { icon: <FaTimesCircle className="text-red-500" />, text: 'Suspended', color: 'text-red-300' },
  pending: { icon: <FaHourglassHalf className="text-yellow-500" />, text: 'Pending', color: 'text-yellow-300' }
};

export default function SuperAdminRestaurants() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ page: 1, limit: 20, search: '', status: '' });
  const [activeMenu, setActiveMenu] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllRestaurants(filters);
        setRestaurants(data.restaurants);
        setPagination(data.pagination);
      } catch (err) {
        setError('Failed to load restaurants');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurants();
  }, [filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
    // fetchRestaurants will be triggered by filters change
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateRestaurantStatus(id, status);
      setRestaurants(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      setActiveMenu(null);
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleViewDetails = async (id) => {
    setModalLoading(true);
    setShowModal(true);
    try {
      const data = await getRestaurantDetails(id);
      setSelectedRestaurant(data.restaurant || data);
    } catch (err) {
      setSelectedRestaurant({ error: 'Failed to fetch details' });
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-white mb-6">Restaurant Management</h1>

      {/* Filters and Search */}
      <div className="mb-6 p-4 bg-gray-800 rounded-lg flex flex-col md:flex-row items-center justify-between gap-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
            />
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          <select
            value={filters.status}
            onChange={e => setFilters(prev => ({ ...prev, page: 1, status: e.target.value }))}
            className="bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 w-full sm:w-auto"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
          <button 
            type="submit" 
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg w-full sm:w-auto"
          >
            Search
          </button>
        </form>
        <button 
          onClick={() => setFilters(prev => ({ ...prev, page: 1 }))} 
          disabled={loading} 
          className="p-2 text-gray-400 hover:text-white transition bg-gray-700 rounded-lg"
          title="Refresh"
        >
          <FaSync className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Restaurants Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Restaurant</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Owner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Joined On</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-gray-400">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-red-400">
                  {error}
                </td>
              </tr>
            ) : restaurants.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-gray-400">
                  No restaurants found
                </td>
              </tr>
            ) : (
              [...restaurants]
                .sort((a, b) => a.id.localeCompare(b.id))
                .map(r => (
                  <tr key={r.id} className="hover:bg-gray-700/50 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {r.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className="flex items-center gap-2">
                        {r.owner?.displayName || 'N/A'}
                        {r.ownerMissing && (
                          <span title="Owner account deleted" className="ml-1 text-yellow-400" style={{ cursor: 'help' }}>
                            ⚠️
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`flex items-center gap-2 ${statusStyles[r.status]?.color || 'text-gray-400'}`}>
                        {statusStyles[r.status]?.icon} {statusStyles[r.status]?.text || r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {r.createdAt
                        ? (r.createdAt._seconds
                            ? new Date(r.createdAt._seconds * 1000).toLocaleString()
                            : (typeof r.createdAt === 'string' 
                                ? new Date(r.createdAt).toLocaleString() 
                                : 'N/A'))
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg text-xs"
                          onClick={() => handleViewDetails(r.id)}
                        >
                          View
                        </button>
                        <div className="relative">
                          <button 
                            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
                            onClick={() => setActiveMenu(activeMenu === r.id ? null : r.id)}
                          >
                            <FaEllipsisV />
                          </button>
                          {activeMenu === r.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-10 border border-gray-700">
                              <div className="py-1">
                                <button
                                  onClick={() => handleStatusChange(r.id, 'active')}
                                  className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 w-full text-left"
                                >
                                  Set as Active
                                </button>
                                <button
                                  onClick={() => handleStatusChange(r.id, 'suspended')}
                                  className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 w-full text-left"
                                >
                                  Suspend
                                </button>
                                <button
                                  onClick={() => handleStatusChange(r.id, 'pending')}
                                  className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 w-full text-left"
                                >
                                  Set as Pending
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <p>
            Showing <span className="font-bold text-white">
              {((filters.page - 1) * filters.limit) + 1}
            </span> to <span className="font-bold text-white">
              {Math.min(filters.page * filters.limit, pagination.total)}
            </span> of <span className="font-bold text-white">
              {pagination.total}
            </span> results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters(p => ({...p, page: p.page - 1}))}
              disabled={filters.page <= 1}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600 transition"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters(p => ({...p, page: p.page + 1}))}
              disabled={filters.page >= pagination.pages}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal for restaurant details */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowModal(false)}>
          <div 
            className="bg-gray-900 rounded-lg p-6 min-w-[350px] max-w-[90vw] max-h-[90vh] overflow-y-auto relative shadow-2xl" 
            onClick={e => e.stopPropagation()}
          >
            <button 
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl" 
              onClick={() => setShowModal(false)}
            >
              ✕
            </button>
            {modalLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : selectedRestaurant?.error ? (
              <div className="text-red-400 text-center py-8">{selectedRestaurant.error}</div>
            ) : selectedRestaurant ? (
              <div className="text-white space-y-4">
                <h2 className="text-2xl font-bold mb-4 text-center text-purple-400">{selectedRestaurant.name}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="font-bold text-lg mb-2 text-gray-300 border-b border-gray-700 pb-2">Basic Information</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-400">Status:</span>
                        <span className={`ml-2 capitalize ${statusStyles[selectedRestaurant.status]?.color || 'text-gray-300'}`}>
                          {selectedRestaurant.status}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Owner:</span>
                        <span className="ml-2 flex items-center gap-2">
                          {selectedRestaurant.owner?.displayName || 'N/A'}
                          {selectedRestaurant.ownerMissing && (
                            <span title="Owner account deleted" className="ml-1 text-yellow-400" style={{ cursor: 'help' }}>
                              ⚠️
                            </span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Email:</span>
                        <span className="ml-2">{selectedRestaurant.owner?.email || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Joined On:</span>
                        <span className="ml-2">
                          {selectedRestaurant.createdAt
                            ? (selectedRestaurant.createdAt._seconds
                                ? new Date(selectedRestaurant.createdAt._seconds * 1000).toLocaleString()
                                : (typeof selectedRestaurant.createdAt === 'string' 
                                    ? new Date(selectedRestaurant.createdAt).toLocaleString() 
                                    : 'N/A'))
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="font-bold text-lg mb-2 text-gray-300 border-b border-gray-700 pb-2">Contact Information</h3>
                    <div className="space-y-3">
                      {selectedRestaurant.contactInfo?.address && (
                        <div>
                          <span className="text-gray-400">Address:</span>
                          <span className="ml-2">{selectedRestaurant.contactInfo.address}</span>
                        </div>
                      )}
                      {selectedRestaurant.contactInfo?.phone && (
                        <div>
                          <span className="text-gray-400">Phone:</span>
                          <span className="ml-2">{selectedRestaurant.contactInfo.phone}</span>
                        </div>
                      )}
                      {selectedRestaurant.email && (
                        <div>
                          <span className="text-gray-400">Restaurant Email:</span>
                          <span className="ml-2">{selectedRestaurant.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-6">
                  <button
                    onClick={() => handleStatusChange(selectedRestaurant.id, 'active')}
                    className={`px-4 py-2 rounded-lg ${selectedRestaurant.status === 'active' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                    disabled={selectedRestaurant.status === 'active'}
                  >
                    Set Active
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedRestaurant.id, 'suspended')}
                    className={`px-4 py-2 rounded-lg ${selectedRestaurant.status === 'suspended' ? 'bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                    disabled={selectedRestaurant.status === 'suspended'}
                  >
                    Suspend
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedRestaurant.id, 'pending')}
                    className={`px-4 py-2 rounded-lg ${selectedRestaurant.status === 'pending' ? 'bg-yellow-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                    disabled={selectedRestaurant.status === 'pending'}
                  >
                    Set Pending
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}